/**
 * Storage serialization for the cached user object.
 *
 * NOTE ON THE FILENAME: this module previously performed AES-GCM "encryption" of
 * localStorage data. It no longer encrypts anything. The path is retained so the
 * change stays a small, reviewable diff rather than a rename touching importers.
 *
 * Why the encryption was removed (register item S02):
 *
 * The key was derived from `REACT_APP_ENCRYPTION_KEY` with a hardcoded fallback
 * literal (deliberately not reproduced here — quoting it would ship the same
 * string in this file's sourcemap), alongside a static salt. Both shipped inside
 * the JavaScript bundle, so any party able to read the ciphertext could also read
 * the key and salt needed to decrypt it. More fundamentally, the auth bearer
 * token sits unencrypted in the same localStorage: an attacker with read access
 * to that store already holds the credential, which makes an encrypted user
 * profile beside it of no additional protective value.
 *
 * It therefore provided obfuscation, not confidentiality, at the cost of
 * implying a security control that did not exist. Removing it is the honest
 * position; the real remediation for localStorage exposure is server-issued
 * httpOnly cookies (register item S01), which requires backend changes.
 */

/** Prefix marking values written by this module, so legacy blobs are detectable. */
const FORMAT_MARKER = 'v2:';

/**
 * Serializes the user object for storage.
 *
 * @param obj Plain user object as returned by the API.
 */
export async function serializeUser(obj: unknown): Promise<string> {
  return `${FORMAT_MARKER}${JSON.stringify(obj)}`;
}

/**
 * Reads a stored user object.
 *
 * Values written by the previous AES-GCM implementation are **discarded rather
 * than migrated**. Decrypting them would require retaining the hardcoded key
 * that this change exists to remove, and discarding is safe: `AuthContext`
 * already handles "token present, no cached user" by re-fetching the profile
 * from `/auth/profile` (the `else if (savedToken)` branch of its bootstrap), so
 * affected sessions stay signed in and simply refresh their profile once.
 *
 * @param stored Raw string from localStorage.
 * @returns The parsed user object, or `null` for absent, legacy or unparseable data.
 */
export async function parseStoredUser<T = unknown>(stored: string | null): Promise<T | null> {
  if (!stored) return null;

  if (!stored.startsWith(FORMAT_MARKER)) {
    // Legacy AES-GCM ciphertext, or anything else unrecognised — drop it.
    return null;
  }

  try {
    return JSON.parse(stored.slice(FORMAT_MARKER.length)) as T;
  } catch {
    return null;
  }
}
